import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useState } from 'react';
import {
  ScopeableType,
  useGetAllPackagesQuery,
  useProductOfferingsQuery,
} from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import { normalizeScopes } from '../../utils/scope-utils';
import {
  ScopeItem,
  SCOPE_PROGRAM_OPTIONS,
} from '../form-fields/scope-selector/scope-selector-constants';
import { TextGroupingChildren } from '../utils/text-grouping';
import {
  SCOPE_DISPLAY_LIST_TEST_ID,
  SCOPE_DISPLAY_TEST_ID,
  SCOPE_DISPLAY_TOGGLE_TEST_ID,
} from './scope-display-constants';

export interface ScopeDisplayProps {
  scopes?: ScopeItem[] | null;
}

interface ResolvedScope {
  type: ScopeableType;
  name: string;
}

const typeLabelSingular: Record<ScopeableType, string> = {
  [ScopeableType.Global]: 'Global',
  [ScopeableType.Package]: 'Package',
  [ScopeableType.ProductOffering]: 'Product offering',
  [ScopeableType.Program]: 'Program',
};

const typeLabelPlural: Record<ScopeableType, string> = {
  [ScopeableType.Global]: 'Global',
  [ScopeableType.Package]: 'packages',
  [ScopeableType.ProductOffering]: 'product offerings',
  [ScopeableType.Program]: 'programs',
};

const formatSingleScopeText = (scope: ResolvedScope): string => {
  if (scope.type === ScopeableType.Global) return 'Global';
  return `${typeLabelSingular[scope.type]} — ${scope.name}`;
};

export const ScopeDisplay = ({
  scopes,
}: ScopeDisplayProps): JSX.Element | null => {
  const [expanded, setExpanded] = useState(false);
  const { data: packagesData } = useGetAllPackagesQuery();
  const { data: offeringsData } = useProductOfferingsQuery();

  const resolveName = (scope: ScopeItem): string => {
    if (scope.scopeableId == null) return '';
    switch (scope.scopeableType) {
      case ScopeableType.Package: {
        const pkg = packagesData?.getAllPackages?.find(
          p => p.id === scope.scopeableId,
        );
        return pkg
          ? capitalizeFirstLetterAndSpaceSnakeString(pkg.name)
          : `#${scope.scopeableId}`;
      }
      case ScopeableType.ProductOffering: {
        const offering = offeringsData?.productOfferings?.find(
          o => o.id === scope.scopeableId,
        );
        return offering?.name ?? `#${scope.scopeableId}`;
      }
      case ScopeableType.Program: {
        const program = SCOPE_PROGRAM_OPTIONS.find(
          p => p.id === scope.scopeableId,
        );
        return program?.name ?? `#${scope.scopeableId}`;
      }
      default:
        return '';
    }
  };

  const resolved: ResolvedScope[] = normalizeScopes(scopes).map(s => ({
    type: s.scopeableType,
    name: resolveName(s),
  }));

  if (resolved.length === 0) return null;

  if (resolved.length === 1) {
    return (
      <TextGroupingChildren label="Scope">
        <span data-testid={SCOPE_DISPLAY_TEST_ID}>
          {formatSingleScopeText(resolved[0])}
        </span>
      </TextGroupingChildren>
    );
  }

  const uniformType = resolved.every(r => r.type === resolved[0].type)
    ? resolved[0].type
    : null;
  const summaryLabel = uniformType
    ? `${resolved.length} ${typeLabelPlural[uniformType]}`
    : `${resolved.length} scopes`;

  return (
    <HHStack
      direction="column"
      spacing={0.5}
      data-testid={SCOPE_DISPLAY_TEST_ID}
    >
      <HHTypography hhVariant="muted" fontSize={12}>
        Scope
      </HHTypography>
      <HHStack direction="row" alignItems="center" spacing={1}>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="success"
          fontSize={12}
        >
          {summaryLabel}
        </HHTypography>
        <HHButton
          hhVariant="variant-bypass"
          variant="text"
          size="small"
          color="primary"
          data-testid={SCOPE_DISPLAY_TOGGLE_TEST_ID}
          onClick={(): void => setExpanded(prev => !prev)}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={{ minWidth: 0, padding: '0 4px', fontSize: 12 }}
        >
          {expanded ? 'Hide' : 'See all'}
        </HHButton>
      </HHStack>
      {expanded && (
        <ul
          data-testid={SCOPE_DISPLAY_LIST_TEST_ID}
          style={{ paddingLeft: 24, margin: 0, listStyle: 'disc' }}
        >
          {resolved.map((item, idx) => (
            <li key={`${item.type}-${item.name}-${idx}`}>
              <HHTypography
                hhVariant="variant-bypass"
                variant="body2"
                fontSize={12}
              >
                {item.type === ScopeableType.Global || uniformType
                  ? item.name || typeLabelSingular[item.type]
                  : formatSingleScopeText(item)}
              </HHTypography>
            </li>
          ))}
        </ul>
      )}
    </HHStack>
  );
};
